import type {ReactElement} from 'react';
import {useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import styles from './media-hover-preview.module.scss';
import type {MediaHoverPreviewProps, PreviewPlacement} from './media-hover-preview.types';
import {
  MEDIA_HOVER_PREVIEW_ANCHOR_GAP,
  MEDIA_HOVER_PREVIEW_CARD_PADDING,
  MEDIA_HOVER_PREVIEW_MAX_VIEWPORT_FRACTION,
  MEDIA_HOVER_PREVIEW_OPEN_DELAY_MS,
  MEDIA_HOVER_PREVIEW_SCALE,
  MEDIA_HOVER_PREVIEW_VIEWPORT_MARGIN,
  MediaHoverPreviewMediaType,
} from './media-hover-preview.types';

const clampToRange = (value: number, min: number, max: number): number => {
  return Math.min(Math.max(value, min), max);
};

const getPlacementForAnchor = (anchorRect: DOMRect, scale: number): PreviewPlacement => {
  const width = Math.min(anchorRect.width * scale, window.innerWidth * MEDIA_HOVER_PREVIEW_MAX_VIEWPORT_FRACTION);
  const height = Math.min(anchorRect.height * scale, window.innerHeight * MEDIA_HOVER_PREVIEW_MAX_VIEWPORT_FRACTION);

  let left = anchorRect.right + MEDIA_HOVER_PREVIEW_ANCHOR_GAP;
  if (left + width > window.innerWidth - MEDIA_HOVER_PREVIEW_VIEWPORT_MARGIN) {
    left = anchorRect.left - MEDIA_HOVER_PREVIEW_ANCHOR_GAP - width;
  }
  if (left < MEDIA_HOVER_PREVIEW_VIEWPORT_MARGIN) {
    left = clampToRange(anchorRect.left + anchorRect.width / 2 - width / 2, MEDIA_HOVER_PREVIEW_VIEWPORT_MARGIN, window.innerWidth - width - MEDIA_HOVER_PREVIEW_VIEWPORT_MARGIN);
  }

  const top = clampToRange(
    anchorRect.top + anchorRect.height / 2 - height / 2,
    MEDIA_HOVER_PREVIEW_VIEWPORT_MARGIN,
    window.innerHeight - height - MEDIA_HOVER_PREVIEW_VIEWPORT_MARGIN
  );

  return {top, left, width, height};
};

export function MediaHoverPreview({
  previewSrc,
  mediaType = MediaHoverPreviewMediaType.IMAGE,
  isEnabled = true,
  className = '',
  getPreviewContent,
  shouldMeasureWrapper = false,
  scale = MEDIA_HOVER_PREVIEW_SCALE,
  children,
}: MediaHoverPreviewProps): ReactElement {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const openTimer = useRef<number | null>(null);
  const [placement, setPlacement] = useState<PreviewPlacement | null>(null);

  const cancelOpenTimer = (): void => {
    if (openTimer.current !== null) {
      window.clearTimeout(openTimer.current);
      openTimer.current = null;
    }
  };

  useEffect(() => {
    return (): void => {
      cancelOpenTimer();
    };
  }, []);

  useEffect(() => {
    if (placement === null) {
      return undefined;
    }

    const closePreview = (): void => {
      setPlacement(null);
    };
    window.addEventListener('scroll', closePreview, true);
    window.addEventListener('pointerdown', closePreview, true);
    return (): void => {
      window.removeEventListener('scroll', closePreview, true);
      window.removeEventListener('pointerdown', closePreview, true);
    };
  }, [placement]);

  const getAnchorRect = (): DOMRect | null => {
    const wrapper = wrapperRef.current;
    if (!wrapper) {
      return null;
    }

    if (!shouldMeasureWrapper) {
      const media = wrapper.querySelector('img, video, canvas');
      const mediaRect = media?.getBoundingClientRect();
      if (mediaRect && mediaRect.width > 0 && mediaRect.height > 0) {
        return mediaRect;
      }
    }

    const wrapperRect = wrapper.getBoundingClientRect();
    return wrapperRect.width > 0 && wrapperRect.height > 0 ? wrapperRect : null;
  };

  const onMouseEnter = (): void => {
    if (!isEnabled) {
      return;
    }
    cancelOpenTimer();
    openTimer.current = window.setTimeout(() => {
      openTimer.current = null;
      const anchorRect = getAnchorRect();
      if (anchorRect) {
        setPlacement(getPlacementForAnchor(anchorRect, scale));
      }
    }, MEDIA_HOVER_PREVIEW_OPEN_DELAY_MS);
  };

  const onMouseLeave = (): void => {
    cancelOpenTimer();
    setPlacement(null);
  };

  const getPreviewMedia = (cardPlacement: PreviewPlacement): ReactElement => {
    if (getPreviewContent) {
      return (
        <div className={styles.previewContent}>
          {getPreviewContent({
            width: cardPlacement.width - 2 * MEDIA_HOVER_PREVIEW_CARD_PADDING,
            height: cardPlacement.height - 2 * MEDIA_HOVER_PREVIEW_CARD_PADDING,
          })}
        </div>
      );
    }
    if (mediaType === MediaHoverPreviewMediaType.VIDEO) {
      return <video className={styles.previewMedia} src={previewSrc} autoPlay loop muted playsInline />;
    }
    return <img className={styles.previewMedia} src={previewSrc} alt="" />;
  };

  const getPreviewCard = (): ReactElement | null => {
    if (placement === null) {
      return null;
    }
    return createPortal(
      <div aria-hidden="true" className={styles.previewCard} style={{height: placement.height, left: placement.left, top: placement.top, width: placement.width}}>
        {getPreviewMedia(placement)}
      </div>,
      document.body
    );
  };

  return (
    <div ref={wrapperRef} className={className} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      {children}
      {getPreviewCard()}
    </div>
  );
}
